import React from 'react';
import Home from './pages/Home';
import Design from './pages/Design';
import TableScreen from './pages/TableScreen';
import { TABLE_SCREEN_LIST } from './pages/tableScreens';
import Overview from './pages/Overview';
import Signups from './pages/Signups';
import Signup from './pages/Signup';
import Users from './pages/Users';
import User from './pages/User';
import Purchases from './pages/Purchases';
import Purchase from './pages/Purchase';
import Emails from './pages/Emails';
import Email from './pages/Email';
import Participants from './pages/Participants';
import Reports from './pages/Reports';
import Play from './pages/Play';
import SeasonalCourts from './pages/SeasonalCourts';
import SeasonalOverview from './pages/SeasonalOverview';
import Transaction from './pages/Transaction';
import PaymentMethod from './pages/PaymentMethod';
import Subscription from './pages/Subscription';
import Participant from './pages/Participant';
import ParticipantCreate from './pages/ParticipantCreate';
import Provider from './pages/Provider';
import Facility from './pages/Facility';
import Area from './pages/Area';
import FacilityRedirect from './pages/FacilityRedirect';
import ProviderAdmin from './pages/ProviderAdmin';
import SeoText from './pages/SeoText';
import FacilityText from './pages/FacilityText';
import Activity from './pages/Activity';
import SeasonalCourtPurchase from './pages/SeasonalCourtPurchase';
import Crawling from './pages/Crawling';
import ResourceGroup from './pages/ResourceGroup';
import NonPartnerFacility from './pages/NonPartnerFacility';
import BookingAgreement from './pages/BookingAgreement';
import Locker from './pages/Locker';
import RelayRule from './pages/RelayRule';
import SeasonalCourt from './pages/SeasonalCourt';
import SeasonalCourtSlot from './pages/SeasonalCourtSlot';
import ExternalPurchase from './pages/ExternalPurchase';
import ExternalRecord from './pages/ExternalRecord';
import AmbassadorApplications from './pages/AmbassadorApplications';
import PlayEvent from './pages/PlayEvent';
import PlayEventSession from './pages/PlayEventSession';
import Maintenance from './pages/Maintenance';
import MasterCodes from './pages/MasterCodes';
import LockerCreate from './pages/LockerCreate';
import FacilityCreate from './pages/FacilityCreate';
import CrawlingRun from './pages/CrawlingRun';
import Report from './pages/Report';
import DeviceJobs from './pages/DeviceJobs';
import DeviceJob from './pages/DeviceJob';

/**
 * Routes of the rebuilt admin, relative to `/next`.
 *
 * Deliberately separate from `src/routes.js`: a page appears here only once it
 * has been rewritten. Everything else keeps living at its old path, in the old
 * shell, untouched — so there is never a second copy of a page nobody has
 * rebuilt yet, and nothing to keep in sync.
 *
 * The migration of one page is then: add the new component here, compare the
 * two URLs side by side (`/areas` against `/next/areas`), and when the new one
 * wins, point the old path at it and delete the old file. The duplication has
 * an end built into it.
 */
// The five table screens share one component; each gets its own route so the
// URL, the breadcrumb and the nav highlight all behave like separate pages.
const tableRoutes = TABLE_SCREEN_LIST.map(({ key, path, label }) => ({
  path,
  label,
  component: () => <TableScreen screenKey={key} />
}));

export default [
  {
    path: 'maintenance',
    label: 'Maintenance',
    component: Maintenance
  },
  {
    path: 'salto-master-codes',
    label: 'Salto master codes',
    component: MasterCodes
  },
  {
    // Before the event route, so `session` is never read as part of it.
    path: 'play-event/:eventId/session/:sessionId',
    label: 'Pairings',
    component: PlayEventSession
  },
  {
    path: 'play-event/:eventId',
    label: 'Play event',
    component: PlayEvent
  },
  {
    path: 'ambassador-applications',
    label: 'Ambassador applications',
    component: AmbassadorApplications
  },
  {
    path: 'external-purchase/:externalPurchaseId',
    label: 'External purchase',
    component: ExternalPurchase
  },
  {
    path: 'external-purchase-attempt/:recordId',
    label: 'External purchase attempt',
    component: () => <ExternalRecord kind="attempt" />
  },
  {
    path: 'external-activity/:recordId',
    label: 'External activity',
    component: () => <ExternalRecord kind="activity" />
  },
  {
    path: 'seasonal-court/:seasonalCourtId',
    label: 'Seasonal court',
    component: SeasonalCourt
  },
  {
    path: 'seasonal-court-slot/:slotId',
    label: 'Seasonal court slot',
    component: SeasonalCourtSlot
  },
  {
    path: 'relay-rule/:ruleId',
    label: 'Relay rule',
    component: RelayRule
  },
  {
    // Before the detail route, so `new` is never read as a locker id.
    path: 'locker/new',
    label: 'New locker',
    component: LockerCreate
  },
  {
    path: 'locker/:lockerId',
    label: 'Locker',
    component: Locker
  },
  {
    path: 'booking-agreement/:agreementId',
    label: 'Booking agreement',
    component: BookingAgreement
  },
  {
    path: 'non-partner-facility/:nonPartnerFacilityId',
    label: 'Non-partner facility',
    component: NonPartnerFacility
  },
  {
    path: 'resource-group/:resourceGroupId',
    label: 'Resource group',
    component: ResourceGroup
  },
  {
    // Before the detail route, or `run` is read as an id.
    path: 'crawling/run',
    label: 'Run a crawling',
    component: CrawlingRun
  },
  {
    path: 'crawling/:crawlingId',
    label: 'Crawling',
    component: Crawling
  },
  {
    path: 'seasonal-court-purchase/:seasonalCourtPurchaseId',
    label: 'Seasonal court purchase',
    component: SeasonalCourtPurchase
  },
  {
    path: 'activity/:activityId',
    label: 'Activity',
    component: Activity
  },
  {
    // `new` before the detail route, or it would be read as an id.
    path: 'seo-text/new',
    label: 'New SEO text',
    component: SeoText
  },
  {
    path: 'seo-text/:seoTextId',
    label: 'SEO text',
    component: SeoText
  },
  {
    // `new` before the detail route, or it would be read as an id.
    path: 'facility-text/new',
    label: 'New facility text',
    component: FacilityText
  },
  {
    path: 'facility-text/:facilityTextId',
    label: 'Facility text',
    component: FacilityText
  },
  {
    path: 'device-jobs',
    label: 'Device jobs',
    component: DeviceJobs
  },
  {
    path: 'device-job/:jobId',
    label: 'Device job',
    component: DeviceJob
  },
  {
    path: 'home',
    label: 'Home',
    component: Home
  },
  {
    path: 'overview',
    label: 'Overview',
    component: Overview
  },
  {
    path: 'signups',
    label: 'Signups',
    component: Signups
  },
  {
    // Reached from the list, never from the rail — which is why the rail is a
    // separate file (`nav.js`) rather than derived from this one.
    path: 'signup/:signupId',
    label: 'Signup',
    component: Signup
  },
  {
    path: 'emails',
    label: 'Emails',
    component: Emails
  },
  {
    path: 'email/:emailId',
    label: 'Email',
    component: Email
  },
  {
    path: 'users',
    label: 'Users',
    component: Users
  },
  {
    path: 'user/:userId',
    label: 'User',
    component: User
  },
  {
    path: 'participants',
    label: 'Participants',
    component: Participants
  },
  {
    path: 'purchases',
    label: 'Purchases',
    component: Purchases
  },
  {
    path: 'purchase/:purchaseId',
    label: 'Purchase',
    component: Purchase
  },
  {
    path: 'reports',
    label: 'Reports',
    component: Reports
  },
  {
    path: 'reports/sales-new',
    label: 'Sales report',
    component: () => <Report report="sales-new" />
  },
  {
    path: 'reports/sales-old',
    label: 'Historical sales report',
    component: () => <Report report="sales-old" />
  },
  {
    path: 'reports/settlement',
    label: 'Settlement report',
    component: () => <Report report="settlement" />
  },
  {
    path: 'reports/seasonal-courts',
    label: 'Seasonal court report',
    component: () => <Report report="seasonal-courts" />
  },
  {
    path: 'play',
    label: 'Play',
    component: Play
  },
  {
    path: 'seasonal-courts',
    label: 'Seasonal Courts',
    component: SeasonalCourts
  },
  {
    path: 'seasonal-courts-overview',
    label: 'Seasonal Courts Overview',
    component: SeasonalOverview
  },
  {
    // Before the detail route, or `/next/participant/new` would be read as an id.
    path: 'participant/new',
    label: 'Add a participant',
    component: ParticipantCreate
  },
  {
    path: 'participant/:participantId',
    label: 'Participant',
    component: Participant
  },
  {
    // `new` first, or it would be read as an id.
    path: 'area/new',
    label: 'New area',
    component: Area
  },
  {
    path: 'area/:areaId',
    label: 'Area',
    component: Area
  },
  {
    path: 'facility-redirect/new',
    label: 'New redirect',
    component: FacilityRedirect
  },
  {
    path: 'facility-redirect/:redirectId',
    label: 'Redirect',
    component: FacilityRedirect
  },
  {
    // Before the detail route, so `new` is never read as a facility id.
    path: 'facility/new',
    label: 'New facility',
    component: FacilityCreate
  },
  {
    path: 'facility/:facilityId',
    label: 'Facility',
    component: Facility
  },
  {
    path: 'provider-admin/:providerAdminId',
    label: 'Provider admin',
    component: ProviderAdmin
  },
  {
    // Before the detail route, so `new` is never read as a provider id.
    path: 'provider/new',
    label: 'New provider',
    component: Provider
  },
  {
    path: 'provider/:providerId',
    label: 'Provider',
    component: Provider
  },
  {
    path: 'transaction/:transactionId',
    label: 'Transaction',
    component: Transaction
  },
  {
    /*
      One route for every kind, not one per file.

      The list builds this path from the row's own `Type`, and production holds
      five: CreditCard, UserCredit, MobilePay, Frisbii and Undefined. Only two
      had routes, so a third of the payment methods in the admin — fifty
      thousand rows — silently landed on Home when clicked, because the route
      table ends in a catch-all `Navigate` and a missing route looks exactly
      like a link that does nothing today.

      Collapsing them is safe because `getCreditCard` and
      `getPaymentMethodUndefined` post to the same endpoint (REWRITE-STATE.md),
      so the kind never decided what came back — only which four extra fields
      were rendered, which the page can read from the record instead.
    */
    path: 'payment-method/:kind/:paymentMethodId',
    label: 'Payment method',
    component: PaymentMethod
  },
  {
    path: 'subscription/:subscriptionId',
    label: 'Subscription',
    component: Subscription
  },
  ...tableRoutes,
  {
    path: 'design',
    label: 'Design',
    component: Design
  }
];
